import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import { es } from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import SuperTableV2 from "../SuperTableV2";
import { toPlainText } from "./ArtifactTable";
import { RetrospectivasServices } from "./BriefcaseServices";
import { fetchRetrospectivas } from "../../redux";
import planningImg from "../../assets/planning-1.png";

registerLocale("es", es);

// Excluye sábados y domingos del datepicker. Duplicado del helper homónimo de ArtifactTable.jsx (no
// exportado allí) porque ese archivo es intocable (decisión spec 004 §6.1, ratificada en spec 005 §6.1).
// Es una función pura de 3 líneas: duplicarla es más barato que reabrir esa decisión. Mismo criterio usado
// en AprendizajesBriefcase.jsx.
const isWeekday = (date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

const toDateInputValue = (val) => (val ? String(val).slice(0, 10) : "");

// Matriz bespoke "Retrospectivas" (spec 005). NO delega en ArtifactTable.jsx (decisión cerrada §6.1): el
// genérico compartido por Riesgos/Compromisos/Novedades no soporta bloquear un campo en modo edición
// (RF-5/RF-7). Subconjunto estricto de AprendizajesBriefcase.jsx: sin catálogo/DDL, sin wizard de
// reutilización, sin auto-referencia. Solo 2 campos (Sprint/Ciclo + Fecha); en edición solo Fecha es
// editable. Reutiliza la maquinaria transversal: SuperTableV2, toPlainText (anti-XSS, H-05), isWeekday +
// DatePicker, swal, sub-slice Redux (fetchRetrospectivas) y la prop `proyectos` ya cargada por
// EditBriefcase (sin endpoint nuevo para "Sprint/Ciclo").
const RetrospectivasBriefcase = ({ idPortafolio, proyectos = [] }) => {
  const [t] = useTranslation("briefcase");
  const dispatch = useDispatch();
  const slice = useSelector((state) => state.briefcaseArtifacts.retrospectivas);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (idPortafolio) {
      dispatch(fetchRetrospectivas(idPortafolio));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPortafolio]);

  // Opciones dinámicas del select "Sprint / Ciclo" ({ value, label }), poblado con la prop `proyectos`
  // (homólogo de IdProyecto en Impedimentos/Aprendizajes, sin endpoint nuevo).
  const dynamicOptions = useMemo(
    () => ({
      proyectos: (proyectos || []).map((p) => ({ value: p.IdProyecto, label: p.Nombre })),
    }),
    [proyectos]
  );

  const items = slice.items || [];

  const resetForm = () => ({
    IdProyecto: "",
    Fecha: "",
  });

  const openCreate = () => {
    setEditingId(null);
    setFormData(resetForm());
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row.IdRetrospectiva);
    setFormData({
      IdProyecto: row.IdProyecto || "",
      Fecha: toDateInputValue(row.Fecha),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({});
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!formData.IdProyecto || !formData.Fecha) return "required";
    return null;
  };

  const handleSave = async () => {
    if (validate()) {
      swal(t("artefactos_error_title"), t("artefactos_validation_required"), "warning");
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        // RF-5: solo Fecha viaja al backend en edición; el SP tampoco declara @IdProyecto.
        await RetrospectivasServices.update(idPortafolio, editingId, {
          Fecha: formData.Fecha,
        });
      } else {
        await RetrospectivasServices.create(idPortafolio, {
          IdProyecto: formData.IdProyecto,
          Fecha: formData.Fecha,
        });
      }
      swal(
        t("artefactos_done_title"),
        editingId ? t("artefactos_done_update") : t("artefactos_done_create"),
        "success"
      );
      closeModal();
      dispatch(fetchRetrospectivas(idPortafolio));
    } catch (e) {
      swal(t("artefactos_error_title"), t("artefactos_error_save"), "error");
    } finally {
      setSaving(false);
    }
  };

  // RF-6: borrado físico simple, sin integridad referencial (no hay 409 en este artefacto).
  const handleDelete = (row) => {
    swal({
      title: t("artefactos_swal_delete_title"),
      text: t("artefactos_swal_delete_text"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirm) => {
      if (!confirm) return;
      try {
        await RetrospectivasServices.remove(idPortafolio, row.IdRetrospectiva);
        swal(t("artefactos_done_title"), t("artefactos_done_delete"), "success");
        dispatch(fetchRetrospectivas(idPortafolio));
      } catch (e) {
        swal(t("artefactos_error_title"), t("artefactos_error_delete"), "error");
      }
    });
  };

  // RF-3: solo 3 columnas (Sprint/Ciclo, Fecha, Acciones). Sin columna ID; sin botón "Ver detalle".
  const tableColumns = [
    {
      caption: t("artefactos_actions"),
      selector: "__actions",
      width: 110,
      align: "center",
      customRender: (rowWrap) => {
        const row = rowWrap.data;
        return (
          <div className="d-flex justify-content-center">
            <Button
              variant="outline-secondary"
              size="sm"
              className="mr-2"
              onClick={() => openEdit(row)}
              title={t("artefactos_btn_edit")}
            >
              <i className="fa fa-pen" />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDelete(row)}
              title={t("artefactos_btn_delete")}
            >
              <i className="fa fa-trash" />
            </Button>
          </div>
        );
      },
    },
    {
      caption: t("retrospectivas_col_sprint"),
      selector: "NombreProyecto",
      width: 280,
      align: "center",
      customRender: (rowWrap) => (
        <span title={toPlainText(rowWrap.data.NombreProyecto)}>
          {toPlainText(rowWrap.data.NombreProyecto)}
        </span>
      ),
    },
    {
      caption: t("retrospectivas_col_fecha"),
      selector: "Fecha",
      width: 160,
      align: "center",
      customRender: (rowWrap) => <span>{toDateInputValue(rowWrap.data.Fecha)}</span>,
    },
  ];

  return (
    <div>
      {slice.loading ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" variant="danger" />
        </div>
      ) : slice.error ? (
        <p className="text-center text-muted py-4">{t("artefactos_error_load")}</p>
      ) : items.length > 0 ? (
        <SuperTableV2 data={items} columns={tableColumns} title_searchbox={t("artefactos_searchbox")} />
      ) : (
        // RF-9: estado vacío ilustrado con botón de alta.
        <div className="text-center pt-4 mt-4">
          <img
            src={planningImg}
            alt=""
            className="img-fluid mb-3 animated pulse slower infinite"
            width="200"
          />
          <h5>{t("retrospectivas_empty_title")}</h5>
          <p>{t("retrospectivas_empty_desc")}</p>
          <Button onClick={openCreate}>
            <i className="fa fa-plus-circle mr-2"></i>
            {t("retrospectivas_empty_btn")}
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <Button
          variant="success"
          className="position-fixed shadow animate__animated animate__fadeInUp"
          style={{ bottom: "1rem", right: "1rem" }}
          onClick={openCreate}
        >
          <i className="fa fa-plus-circle mr-2" />
          {t("retrospectivas_empty_btn")}
        </Button>
      )}

      <Modal show={showModal} onHide={closeModal} centered contentClassName="border-0 shadow-lg">
        <Modal.Header>
          <Modal.Title className="h5">
            {editingId ? t("artefactos_modal_edit_title") : t("artefactos_modal_create_title")}
          </Modal.Title>
          <Button variant="light" onClick={closeModal} aria-label="Close">
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6} className="mb-3 d-flex flex-column">
                <Form.Label>
                  {t("retrospectivas_label_sprint")}
                  <span className="text-primary"> *</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  disabled={!!editingId}
                  value={formData.IdProyecto || ""}
                  onChange={(e) => handleChange("IdProyecto", e.target.value)}
                >
                  <option value="">{t("retrospectivas_sprint_select")}</option>
                  {(dynamicOptions.proyectos || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Control>
              </Col>
              <Col md={6} className="mb-3 d-flex flex-column">
                <Form.Label>
                  {t("retrospectivas_label_fecha")}
                  <span className="text-primary"> *</span>
                </Form.Label>
                <DatePicker
                  selected={formData.Fecha ? new Date(`${formData.Fecha}T00:00:00`) : null}
                  onChange={(date) => {
                    if (!date) {
                      handleChange("Fecha", "");
                    } else {
                      const y = date.getFullYear();
                      const m = String(date.getMonth() + 1).padStart(2, "0");
                      const d = String(date.getDate()).padStart(2, "0");
                      handleChange("Fecha", `${y}-${m}-${d}`);
                    }
                  }}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  placeholderText="dd/mm/aaaa"
                  autoComplete="off"
                  isClearable
                  popperProps={{ strategy: "fixed" }}
                  locale="es"
                  filterDate={isWeekday}
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={80}
                />
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="dark" onClick={closeModal} disabled={saving}>
            <i className="fa fa-times mr-2" />
            {t("artefactos_btn_cancel")}
          </Button>
          <Button variant="success" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <i className="fa fa-save mr-2" />
                {t("artefactos_btn_save")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RetrospectivasBriefcase;
